import React, { FC, JSX, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { Link, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageSection from "@app/shared/PageSection";
import PageInner from "@app/shared/PageInner";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { ISO_DATE_LOCALE } from "@app/operations/snowflake-data-sharing/constants";
import {
  DataShareBatchStatus,
  DataShareDatabaseStatus,
  DataShareListingPublishedStatus,
  DataShareStatus,
  Tabs,
  TriggerListingRefreshOutcome,
} from "@app/operations/snowflake-data-sharing/enums";
import { SnowflakeDataSharingContext } from "@app/operations/snowflake-data-sharing/SnowflakeDataSharingContext";
import { SnowflakeDataSharingService } from "@app/operations/snowflake-data-sharing/snowflakeDataSharingService";
import {
  ClientDataShareSummary,
  NextRunProject,
  SharingRunBatch,
} from "@app/operations/snowflake-data-sharing/interfaces";
import { useGetClientDataShareStatus } from "@app/data-share/hooks/useGetClientDataShareStatus";
import { Waffles } from "@app/shared/waffles/Waffles";
import { ColumnConfig, SortDirections, WafflesPaginationProps, wafflesKeyColumnName } from "@app/shared/waffles";
import { InfoBadge } from "@app/shared/InfoBadge";
import { QesColors } from "@app/shared/qes-standards/colors";
import { QESButton } from "@app/shared/qes-standards/Button";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { formatDateCustom } from "@app/shared/formatDateTime";
import SingleSelectDropdown from "@app/shared/checkbox-dropdown/SingleSelectDropdown";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import MultiselectDropdown from "@app/shared/checkbox-dropdown/MultiselectDropdown";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import { FlyoutLink } from "@app/shared/Flyouts";
import { BatchProjectStatusesFlyoutBody } from "@app/operations/snowflake-data-sharing/BatchProjectStatusesFlyoutBody";

interface ClientsWaffleToppings {
  key: string;
  clientName: string;
  dataShareStatus: JSX.Element;
  dataShareStatusSortKey: string;
  lastRefreshTime: string;
  lastRefreshTimeSortKey: string;
  totalProjectsInDataShare: number;
}

const clientsColumnKeys = {
  clientName: "clientName",
  dataShareStatus: "dataShareStatus",
  dataShareStatusSortKey: "dataShareStatusSortKey",
  lastRefreshTime: "lastRefreshTime",
  lastRefreshTimeSortKey: "lastRefreshTimeSortKey",
  totalProjectsInDataShare: "totalProjectsInDataShare",
} as const satisfies Record<string, keyof ClientsWaffleToppings>;

const unknownStatusContent = "Unknown";

const renderDataShareStatusBadge = (isDataShareActive: boolean, testId: string, isLoading = false, isError = false): JSX.Element =>
  renderStatusBadge(DataShareStatus.Active, DataShareStatus.Inactive, isDataShareActive, testId, isLoading, isError);

const renderDataShareDatabaseStatusBadge = (isDataShareDatabaseReady: boolean, testId: string, isLoading: boolean, isError: boolean): JSX.Element =>
  renderStatusBadge(DataShareDatabaseStatus.Ready, DataShareDatabaseStatus.NotProvisioned, isDataShareDatabaseReady, testId, isLoading, isError);

const renderDataShareListingPublishedStatusBadge = (isDataSharePublished: boolean, testId: string, isLoading: boolean, isError: boolean): JSX.Element =>
  renderStatusBadge(DataShareListingPublishedStatus.Published, DataShareListingPublishedStatus.Unpublished, isDataSharePublished, testId, isLoading, isError);

const renderStatusBadge = (
  trueStateContent: string | JSX.Element,
  falseStateContent: string |JSX.Element,
  state: boolean,
  testId: string,
  isLoading = false,
  isError = false,
): JSX.Element => {
  const isTrueState = state && !isLoading && !isError;
  const falseOrUnknownContent = isError ? unknownStatusContent : falseStateContent;

  return (
    <InfoBadge
      testId={testId}
      content={isLoading ? <LoadingSpinner /> : isTrueState ? trueStateContent : falseOrUnknownContent}
      icon={isLoading ? null : QesIcons.Circle}
      iconColor={isTrueState ? QesColors.QesPrimaryGreen : QesColors.QesPrimaryGray6}
      iconSide="left"
      iconSize="0.5rem"
      backgroundColor={isTrueState ? QesColors.QesTertiaryGreen4 : QesColors.QesPrimaryGray3}
      borderColor={isTrueState ? QesColors.QesPrimaryGreen : QesColors.QesPrimaryGray5}
      contentColor={isTrueState ? QesColors.QesPrimaryGreen : QesColors.QesPrimaryGray6}
    />
  );
};

export const buildClientsWaffleData = (
  clients: ClientDataShareSummary[], lastRefreshTime?: string, totalProjectsInDataShare?: number,
): ClientsWaffleToppings[] => {
  return clients.map<ClientsWaffleToppings>((client) => {
    const status = client.isDataShareActive ? DataShareStatus.Active : DataShareStatus.Inactive;

    /* oxlint-disable sort-keys */
    return {
      clientName: client.name ?? "",
      dataShareStatus: renderDataShareStatusBadge(client.isDataShareActive, `data-share-status-${client.accountName}`),
      dataShareStatusSortKey: status,
      totalProjectsInDataShare: totalProjectsInDataShare ?? 0,
      lastRefreshTime: !!lastRefreshTime ? formatDateCustom(lastRefreshTime, "MMMM D h:mm A") : "—",
      lastRefreshTimeSortKey: lastRefreshTime ?? "",
      [wafflesKeyColumnName]: client.accountName,
    };
    /* oxlint-enable sort-keys */
  });
};

const compareTimestamps = (a: string, b: string): number => {
  const timeA = !a ? Number.NaN : new Date(a).getTime();
  const timeB = !b ? Number.NaN : new Date(b).getTime();
  const aMissing = Number.isNaN(timeA);
  const bMissing = Number.isNaN(timeB);

  if (aMissing && bMissing) {
    return 0;
  }

  if (aMissing) {
    return 1;
  }

  if (bMissing) {
    return -1;
  }

  return timeA - timeB;
};

const createSortHandler = <T,>(
  comparators: Partial<Record<keyof T, (a: T, b: T) => number>>,
) => (
  segment: T[],
  column: keyof T,
  direction: SortDirections,
): T[] => {
  const comparator = comparators[column];

  if (direction === SortDirections.None || !comparator) {
    return segment;
  }

  const directionScalar = direction === SortDirections.Asc ? 1 : -1;
  return [...segment].sort((a, b) => comparator(a, b) * directionScalar);
};

/* oxlint-disable sort-keys */
const clientsComparators: Partial<Record<keyof ClientsWaffleToppings, (a: ClientsWaffleToppings, b: ClientsWaffleToppings) => number>> = {
  [clientsColumnKeys.clientName]: (a, b) => a.clientName.localeCompare(b.clientName),
  [clientsColumnKeys.dataShareStatus]: (a, b) => a.dataShareStatusSortKey.localeCompare(b.dataShareStatusSortKey),
  [clientsColumnKeys.lastRefreshTime]: (a, b) => a.lastRefreshTimeSortKey.localeCompare(b.lastRefreshTimeSortKey),
  [clientsColumnKeys.totalProjectsInDataShare]: (a, b) => a.totalProjectsInDataShare - b.totalProjectsInDataShare,
};
/* oxlint-enable sort-keys */

export const handleClientsSort = createSortHandler(clientsComparators);

const clientsColumnConfig: ColumnConfig = {
  /* oxlint-disable sort-keys */
  [clientsColumnKeys.clientName]: { displayText: "Client", testId: "client-name" },
  [clientsColumnKeys.dataShareStatus]: { displayText: "Data Share Status", testId: "data-share-status" },
  [clientsColumnKeys.dataShareStatusSortKey]: { hidden: true, testId: "data-share-status-sort-key" },
  [clientsColumnKeys.totalProjectsInDataShare]: { displayText: "Total Projects in Data Share", testId: "total-projects-in-data-share" },
  [clientsColumnKeys.lastRefreshTime]: { displayText: "Last Refresh Time", testId: "last-refresh-time" },
  [clientsColumnKeys.lastRefreshTimeSortKey]: { hidden: true, testId: "last-refresh-time-sort-key" },
  /* oxlint-enable sort-keys */
};

const snowflakeIntegrationFields: Array<{ getValue: (client: ClientDataShareSummary) => string; label: string }> = [
  { getValue: (client) => client.organizationName, label: "Organization Name" },
  { getValue: (client) => client.accountName, label: "Account Name" },
  { getValue: (client) => client.region, label: "Region" },
  { getValue: (client) => client.cloudProvider, label: "Cloud Provider" },
  { getValue: (client) => client.snowflakeEdition ?? "---", label: "Snowflake Edition" },
  { getValue: (client) => client.isHipaaBaaSigned ? "Yes" : "No", label: "HIPAA BAA Signed" },
];

interface NextRunProjectsWaffleToppings {
  datePublished: string;
  datePublishedSortKey: string;
  key: string;
  projectId: number;
  projectLink: JSX.Element;
  projectName: string;
  projectRunConfigurationId: number;
}

const nextRunProjectsColumnKeys = {
  datePublished: "datePublished",
  datePublishedSortKey: "datePublishedSortKey",
  projectId: "projectId",
  projectLink: "projectLink",
  projectName: "projectName",
  projectRunConfigurationId: "projectRunConfigurationId",
} as const satisfies Record<string, keyof NextRunProjectsWaffleToppings>;

const buildNextRunProjectsWaffleData = (projects: NextRunProject[]): NextRunProjectsWaffleToppings[] => {
  return projects.map<NextRunProjectsWaffleToppings>((project) => {
    /* oxlint-disable sort-keys */
    return {
      projectName: project.projectName,
      projectId: project.projectId,
      projectRunConfigurationId: project.projectRunConfigurationId,
      datePublished: formatDateCustom(project.datePublished, "MMMM D h:mm A"),
      datePublishedSortKey: project.datePublished,
      projectLink: <Link to={`/projects/${project.projectId}`}>View project</Link>,
      [wafflesKeyColumnName]: project.projectRunConfigurationId.toString(),
    };
    /* oxlint-enable sort-keys */
  });
};

/* oxlint-disable sort-keys */
const nextRunProjectsComparators: Partial<Record<keyof NextRunProjectsWaffleToppings, (a: NextRunProjectsWaffleToppings, b: NextRunProjectsWaffleToppings) => number>> = {
  [nextRunProjectsColumnKeys.datePublished]: (a, b) => compareTimestamps(a.datePublishedSortKey, b.datePublishedSortKey),
  [nextRunProjectsColumnKeys.projectId]: (a, b) => a.projectId - b.projectId,
  [nextRunProjectsColumnKeys.projectName]: (a, b) => a.projectName.localeCompare(b.projectName),
  [nextRunProjectsColumnKeys.projectRunConfigurationId]: (a, b) => a.projectRunConfigurationId - b.projectRunConfigurationId,
};
/* oxlint-enable sort-keys */

const handleNextRunProjectsSort = createSortHandler(nextRunProjectsComparators);

const nextRunProjectsColumnConfig: ColumnConfig = {
  /* oxlint-disable sort-keys */
  [nextRunProjectsColumnKeys.projectName]: { displayText: "Project Name", testId: "next-run-project-name" },
  [nextRunProjectsColumnKeys.projectId]: { displayText: "Project ID", testId: "next-run-project-id" },
  [nextRunProjectsColumnKeys.projectRunConfigurationId]: { displayText: "Run Configuration ID", testId: "next-run-project-run-configuration-id" },
  [nextRunProjectsColumnKeys.datePublished]: { displayText: "Date Published", testId: "next-run-project-date-published" },
  [nextRunProjectsColumnKeys.datePublishedSortKey]: { hidden: true, testId: "next-run-project-date-published-sort-key" },
  [nextRunProjectsColumnKeys.projectLink]: { displayText: "", noSort: true, testId: "next-run-project-link" },
  /* oxlint-enable sort-keys */
};

interface SharingRunBatchesWaffleToppings {
  key: string;
  batchId: string;
  failedProjectCount: number;
  lastPublishedTimestamp: string;
  lastPublishedTimestampSortKey: string;
  projectCount: number;
  status: JSX.Element;
  statusSortKey: string;
  successPercentage: number;
  viewProjectsLink: JSX.Element;
}

const sharingRunBatchesColumnKeys = {
  batchId: "batchId",
  failedProjectCount: "failedProjectCount",
  lastPublishedTimestamp: "lastPublishedTimestamp",
  lastPublishedTimestampSortKey: "lastPublishedTimestampSortKey",
  projectCount: "projectCount",
  status: "status",
  statusSortKey: "statusSortKey",
  successPercentage: "successPercentage",
  viewProjectsLink: "viewProjectsLink",
} as const satisfies Record<string, keyof SharingRunBatchesWaffleToppings>;

/* oxlint-disable sort-keys */
const dataShareBatchStatusColors: Record<DataShareBatchStatus, { background: QesColors; foreground: QesColors }> = {
  [DataShareBatchStatus.Complete]: { background: QesColors.QesTertiaryGreen4, foreground: QesColors.QesPrimaryGreen },
  [DataShareBatchStatus.Running]: { background: QesColors.QesTertiaryBlue4, foreground: QesColors.QesTertiaryBlue1 },
  [DataShareBatchStatus.Unknown]: { background: QesColors.QesPrimaryGray3, foreground: QesColors.QesPrimaryGray6 },
};
/* oxlint-enable sort-keys */

const renderDataShareBatchStatusBadge = (status: DataShareBatchStatus, testId: string): JSX.Element => {
  const { background, foreground } = dataShareBatchStatusColors[status];

  return (
    <InfoBadge
      testId={testId}
      content={status}
      icon={QesIcons.Circle}
      iconColor={foreground}
      iconSide="left"
      iconSize="0.5rem"
      backgroundColor={background}
      borderColor={foreground}
      contentColor={foreground}
    />
  );
};

const buildSharingRunBatchesWaffleData = (batches: SharingRunBatch[], clientId: number): SharingRunBatchesWaffleToppings[] => {
  return batches.map<SharingRunBatchesWaffleToppings>((batch) => {
    /* oxlint-disable sort-keys */
    return {
      batchId: batch.batchId,
      lastPublishedTimestamp: batch.lastPublishedTimestamp ? formatDateCustom(batch.lastPublishedTimestamp, "MMMM D h:mm A") : "---",
      lastPublishedTimestampSortKey: batch.lastPublishedTimestamp,
      status: renderDataShareBatchStatusBadge(batch.status, `sharing-run-batch-status-${batch.batchId}`),
      statusSortKey: batch.status,
      projectCount: batch.projectCount,
      failedProjectCount: batch.failedProjectCount,
      successPercentage: Math.round(batch.successPercentage * 100) / 100,
      viewProjectsLink: (
        <FlyoutLink
          classNames="text-info"
          content={{
            body: <BatchProjectStatusesFlyoutBody batchId={batch.batchId} clientId={clientId} />,
            title: `Batch ${batch.batchId} Projects`,
          }}
          testId={`sharing-run-batch-view-projects-${batch.batchId}`}
        >
          View Projects
        </FlyoutLink>
      ),
      [wafflesKeyColumnName]: batch.batchId,
    };
    /* oxlint-enable sort-keys */
  });
};

/* oxlint-disable sort-keys */
const sharingRunBatchesComparators: Partial<Record<keyof SharingRunBatchesWaffleToppings, (a: SharingRunBatchesWaffleToppings, b: SharingRunBatchesWaffleToppings) => number>> = {
  [sharingRunBatchesColumnKeys.batchId]: (a, b) => a.batchId.localeCompare(b.batchId),
  [sharingRunBatchesColumnKeys.lastPublishedTimestamp]: (a, b) => compareTimestamps(a.lastPublishedTimestampSortKey, b.lastPublishedTimestampSortKey),
  [sharingRunBatchesColumnKeys.status]: (a, b) => a.statusSortKey.localeCompare(b.statusSortKey),
  [sharingRunBatchesColumnKeys.projectCount]: (a, b) => a.projectCount - b.projectCount,
  [sharingRunBatchesColumnKeys.failedProjectCount]: (a, b) => a.failedProjectCount - b.failedProjectCount,
  [sharingRunBatchesColumnKeys.successPercentage]: (a, b) => a.successPercentage - b.successPercentage,
};
/* oxlint-enable sort-keys */

const handleSharingRunBatchesSort = createSortHandler(sharingRunBatchesComparators);

const sharingRunBatchesColumnConfig: ColumnConfig = {
  /* oxlint-disable sort-keys */
  [sharingRunBatchesColumnKeys.batchId]: { displayText: "Batch ID", testId: "sharing-run-batch-id" },
  [sharingRunBatchesColumnKeys.lastPublishedTimestamp]: { displayText: "Last Published Timestamp", testId: "sharing-run-batch-last-published" },
  [sharingRunBatchesColumnKeys.lastPublishedTimestampSortKey]: { hidden: true, testId: "sharing-run-batch-last-published-sort-key" },
  [sharingRunBatchesColumnKeys.status]: { displayText: "Status", testId: "sharing-run-batch-status" },
  [sharingRunBatchesColumnKeys.statusSortKey]: { hidden: true, testId: "sharing-run-batch-status-sort-key" },
  [sharingRunBatchesColumnKeys.projectCount]: { displayText: "Projects", testId: "sharing-run-batch-project-count" },
  [sharingRunBatchesColumnKeys.failedProjectCount]: { displayText: "Failed Projects", testId: "sharing-run-batch-failed-project-count" },
  [sharingRunBatchesColumnKeys.successPercentage]: { displayText: "Success %", testId: "sharing-run-batch-success-percentage" },
  [sharingRunBatchesColumnKeys.viewProjectsLink]: { displayText: "", noSort: true, testId: "sharing-run-batch-view-projects" },
  /* oxlint-enable sort-keys */
};

const statusFilterItems: DropdownItem<DataShareBatchStatus>[] = Object.values(DataShareBatchStatus).map((status) => ({
  key: status,
  name: status,
}));

const runBatchesPageSizeOptions: DropdownItem<number>[] = [24, 48, 72, 168].map((size) => ({
  key: size,
  name: size.toString(),
}));

const nextRunProjectsPageSize = 25;

const getDefaultPublishedTimestamp = (): string =>
  (d => (d.setDate(d.getDate() - 7), d.toLocaleDateString(ISO_DATE_LOCALE)))(new Date());

export const SnowflakeDataSharing: FC = () => {

  const snowflakeDataSharingContext = useContext(SnowflakeDataSharingContext);
  const history = useHistory();
  const dataSharingService = useRef(new SnowflakeDataSharingService()).current;
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<Tabs>(Tabs.Dashboard);
  const [isTriggeringRefresh, setIsTriggeringRefresh] = useState<boolean>(false);
  const [isQueueingExport, setIsQueueingExport] = useState<boolean>(false);
  const [selectedAccountName, setSelectedAccountName] = useState<string>();
  const [selectedRunStatuses, setSelectedRunStatuses] = useState<Set<DataShareBatchStatus>>(
    () => new Set(Object.values(DataShareBatchStatus)),
  );
  const [publishedTimestampInput, setPublishedTimestampInput] = useState<string>(getDefaultPublishedTimestamp());
  const [submittedPublishedTimestamp, setSubmittedPublishedTimestamp] = useState<string>(getDefaultPublishedTimestamp());
  const [runBatchesPageSize, setRunBatchesPageSize] = useState<number>(24);
  const [runBatchesSkip, setRunBatchesSkip] = useState<number>(0);
  const [runBatchesSortColumn, setRunBatchesSortColumn] = useState<keyof SharingRunBatchesWaffleToppings>();
  const [runBatchesSortDirection, setRunBatchesSortDirection] = useState<SortDirections>(SortDirections.None);
  const [nextRunProjectsSkip, setNextRunProjectsSkip] = useState<number>(0);
  const [nextRunProjectsSortColumn, setNextRunProjectsSortColumn] = useState<keyof NextRunProjectsWaffleToppings>();
  const [nextRunProjectsSortDirection, setNextRunProjectsSortDirection] = useState<SortDirections>(SortDirections.None);

  const [isDatabaseAndListingStatusLoading, setIsDatabaseAndListingStatusLoading] = useState<boolean>(false);

  const clients = snowflakeDataSharingContext.getClients?.() ?? [];
  const lastRefreshTime = snowflakeDataSharingContext.getLastRefreshTime?.();
  const totalProjectsInDataShare = snowflakeDataSharingContext.getTotalProjectsInDataShare?.();
  const clientsTableData = useMemo(
    () => buildClientsWaffleData(clients, lastRefreshTime, totalProjectsInDataShare),
    [clients, lastRefreshTime, totalProjectsInDataShare],
  );

  const clientDropdownItems = useMemo<DropdownItem<string>[]>(
    () => clients.map((client) => ({ key: client.accountName, name: client.name ?? "" })),
    [clients],
  );

  const selectedClient = clients.find((client) => client.accountName === selectedAccountName);

  const nextRunProjectsQuery = useQuery({
    enabled: !!selectedClient,
    queryFn: ({ signal }) => dataSharingService.getNextRunProjects(selectedClient!.id, signal),
    queryKey: ["snowflake-data-sharing-next-run-projects", selectedClient?.id],
    retry: false,
  });

  const dataShareStatusQuery = useGetClientDataShareStatus(selectedClient?.id);

  const nextRunProjectsTableData = useMemo(
    () => buildNextRunProjectsWaffleData(nextRunProjectsQuery.data ?? []),
    [nextRunProjectsQuery.data],
  );

  const sortedNextRunProjects = useMemo(
    () => (!!nextRunProjectsSortColumn
      ? handleNextRunProjectsSort(nextRunProjectsTableData, nextRunProjectsSortColumn, nextRunProjectsSortDirection)
      : nextRunProjectsTableData),
    [nextRunProjectsTableData, nextRunProjectsSortColumn, nextRunProjectsSortDirection],
  );

  const batchesQuery = useQuery({
    enabled: !!selectedClient,
    queryFn: ({ signal }) => dataSharingService.getBatches(selectedClient!.id, submittedPublishedTimestamp, signal),
    queryKey: ["snowflake-data-sharing-batches", selectedClient?.id, submittedPublishedTimestamp],
    retry: false,
  });

  const handleSearchBatches = (): void => {
    setRunBatchesSkip(0);
    if (publishedTimestampInput === submittedPublishedTimestamp) {
      batchesQuery.refetch();
    } else {
      setSubmittedPublishedTimestamp(publishedTimestampInput);
    }
  };

  const sharingRunBatchesTableData = useMemo(
    () => buildSharingRunBatchesWaffleData(
      (batchesQuery.data ?? []).filter((batch) => selectedRunStatuses.has(batch.status)),
      selectedClient?.id ?? 0,
    ),
    [batchesQuery.data, selectedRunStatuses, selectedClient],
  );

  const sortedSharingRunBatches = useMemo(
    () => (!!runBatchesSortColumn
      ? handleSharingRunBatchesSort(sharingRunBatchesTableData, runBatchesSortColumn, runBatchesSortDirection)
      : sharingRunBatchesTableData),
    [sharingRunBatchesTableData, runBatchesSortColumn, runBatchesSortDirection],
  );

  const handleSelectAccount = (accountName: string): void => {
    setSelectedAccountName(accountName);
    setRunBatchesSkip(0);
    setNextRunProjectsSkip(0);
  };

  const handleClientRowClick = (accountName: string): void => {
    handleSelectAccount(accountName);
    setActiveTab(Tabs.SnowflakeIntegration);
  };

  const handleTriggerRefresh = async (): Promise<void> => {
    if (!selectedClient) {
      return;
    }

    setIsTriggeringRefresh(true);
    try {
      const result = await dataSharingService.triggerListingRefresh(selectedClient.id);

      switch (result) {
        case TriggerListingRefreshOutcome.Triggered:
          toast("Refresh triggered.", { containerId: "z-index-9999", type: "success" });
          await queryClient.invalidateQueries({ queryKey: ["snowflake-data-sharing-clients"] });
          break;
        case TriggerListingRefreshOutcome.ListingNotPublished:
          toast("The listing hasn't been published yet, so there was nothing to refresh.", { containerId: "z-index-9999", type: "info" });
          break;
        default:
          toast("Failed to trigger refresh. Please try again.", { containerId: "z-index-9999", type: "error" });
      }
    } catch (error) {
      if ((error as { status?: number })?.status === 400) {
        toast("A data share refresh is already running for this client.", { containerId: "z-index-9999", type: "error" });
      } else {
        toast("Failed to trigger refresh. Please try again.", { containerId: "z-index-9999", type: "error" });
      }
    } finally {
      setIsTriggeringRefresh(false);
    }
  };

  const handleExportAsCsv = async (): Promise<void> => {
    if (!selectedClient) {
      return;
    }

    setIsQueueingExport(true);
    try {
      await dataSharingService.queueViewExport(selectedClient.id);
      toast(<div><p>CSV export queued. The CSVs appear on the Reports page when the export is finished, which may take a while for a large client.</p><p><b>Click to navigate to the reports page.</b></p></div>, {
        containerId: "z-index-9999",
        onClick: () => history.push("/reports"),
        type: "success",
      });
    } catch {
      toast("Failed to queue the export. Please try again.", { containerId: "z-index-9999", type: "error" });
    } finally {
      setIsQueueingExport(false);
    }
  };

  const handleSharingRunBatchesCustomSort = useCallback(
    (
      segment: SharingRunBatchesWaffleToppings[],
      column: keyof SharingRunBatchesWaffleToppings,
      direction: SortDirections,
    ): SharingRunBatchesWaffleToppings[] => {
      setRunBatchesSortColumn(column);
      setRunBatchesSortDirection(direction);
      setRunBatchesSkip(0);
      return segment;
    },
    [],
  );

  const handleNextRunProjectsCustomSort = useCallback(
    (
      segment: NextRunProjectsWaffleToppings[],
      column: keyof NextRunProjectsWaffleToppings,
      direction: SortDirections,
    ): NextRunProjectsWaffleToppings[] => {
      setNextRunProjectsSortColumn(column);
      setNextRunProjectsSortDirection(direction);
      setNextRunProjectsSkip(0);
      return segment;
    },
    [],
  );

  const nextBatchTime = snowflakeDataSharingContext.getNextBatchTime?.();

  const tabs = useMemo(() => {
    return (
      <>
        {
          Object.values(Tabs).map((tab) =>
            <li className={"nav-item"} key={tab}>
              <a
                className={`nav-link ${activeTab === tab ? "active" : ""}`}
                data-testid={`snowflake-data-sharing__tab--${tab.toLowerCase().replaceAll(" ", "-")}`}
                role="button"
                tabIndex={0}
                onClick={() => setActiveTab(tab)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setActiveTab(tab);
                  }
                }}
              >
                {tab}
              </a>
            </li>
          )
        }
      </>
    )
  }, [activeTab])

  const dashboard = (): JSX.Element => {
    return (
      <div className={"dashboard-container"}>
        <div className={"overview-container"}>
          <div className={"clients-with-access"}>
            <h4 className={"header-s"}>Clients with Access</h4>
            <h3 className={"header-m"}>{snowflakeDataSharingContext.getClientsWithAccess?.() ?? "—"}</h3>
            <p className={"label-tiny"}>of {snowflakeDataSharingContext.getConfiguredClients?.() ?? "—"} configured clients</p>
          </div>
          <div className={"projects-in-next-run"}>
            <h4 className={"header-s"}>Projects in Next Run</h4>
            <h3 className={"header-m"}>{snowflakeDataSharingContext.getProjectCount?.() ?? "—"}</h3>
            <p className={"label-tiny"}>across {snowflakeDataSharingContext.getClientCount?.() ?? "—"} clients</p>
          </div>
          <div className={"refresh-health"}>
            <h4 className={"header-s"}>Refresh Health</h4>
            <h3 className={"header-m"}>{snowflakeDataSharingContext.getUnresolvedFailures?.() ?? "—"} failing</h3>
            <p className={"label-tiny"}>unresolved failed refreshes</p>
          </div>
          <div className={"next-batch-window"}>
            <h4 className={"header-s"}>Next Refresh Batch</h4>
            <h3 className={"header-m"}>{nextBatchTime ? formatDateCustom(nextBatchTime, "MMMM D h:mm A") : "—"}</h3>
            <p className={"label-tiny"}>refreshes occur {snowflakeDataSharingContext.getRefreshCadence?.() ?? "—"}</p>
          </div>
        </div>
        <div className={"clients-table-container"}>
          <h4 className={"header-s"}>Clients</h4>
          <Waffles<ClientsWaffleToppings>
            columns={clientsColumnConfig}
            customSort={handleClientsSort}
            data={clientsTableData}
            fullWidth={true}
            onRowClick={(row) => handleClientRowClick(row.key)}
            testId="snowflake-data-sharing-clients"
          />
        </div>
      </div>
    );
  }

  const sharingRunMonitor = (): JSX.Element => {
    const paginationProps: WafflesPaginationProps = {
      changePage: (pageNumber) => setRunBatchesSkip((pageNumber - 1) * runBatchesPageSize),
      recordsPerPage: runBatchesPageSize,
      skip: runBatchesSkip,
      totalRecords: sortedSharingRunBatches.length,
    };

    return (
      <div className={"clients-table-container"}>
        <h4 className={"header-s"}>Snowflake Data Sharing Refresh Runs</h4>
        <div className={"sharing-run-controls"}>
          <SingleSelectDropdown<string>
            containerClassName="sharing-run-control"
            items={clientDropdownItems}
            label="Client"
            onSelectionChanged={(item) => handleSelectAccount(item.key)}
            selectedItem={clientDropdownItems.find((item) => item.key === selectedAccountName) ?? { key: "", name: "Select a client" }}
            testId="sharing-run-client-selector"
          />
          <QESDateInput
            label="Published Since"
            value={publishedTimestampInput}
            onChange={(event) => setPublishedTimestampInput(event.target.value)}
            testId="sharing-run-published-timestamp-input"
          />
          <MultiselectDropdown<DataShareBatchStatus>
            containerClassName="sharing-run-control"
            hideSearch={true}
            items={statusFilterItems}
            label="Status Filters"
            onSelectionChanged={(selectedItems) => {
              setSelectedRunStatuses(new Set(selectedItems.map((item) => item.key)));
              setRunBatchesSkip(0);
            }}
            selectedItems={statusFilterItems.filter((item) => selectedRunStatuses.has(item.key))}
            testId="sharing-run-status-filters"
          />
          <SingleSelectDropdown<number>
            containerClassName="sharing-run-control"
            hideSearch={true}
            items={runBatchesPageSizeOptions}
            label="Rows Per Page"
            onSelectionChanged={(item) => {
              setRunBatchesPageSize(item.key);
              setRunBatchesSkip(0);
            }}
            selectedItem={runBatchesPageSizeOptions.find((option) => option.key === runBatchesPageSize) ?? runBatchesPageSizeOptions[0]}
            testId="sharing-run-page-size"
          />
          <QESButton
            className="align-self-end"
            disabled={!selectedClient}
            onClick={handleSearchBatches}
            qesType="primary"
            qesTestId="sharing-run-search-button"
          >
            Search
          </QESButton>
        </div>
        {batchesQuery.isFetching &&
          <div className={"d-flex justify-content-center"}>
            <LoadingSpinner />
          </div>
        }
        {!batchesQuery.isFetching && batchesQuery.isError &&
          <QESAlert type="danger" persist={true}>
            There was an error loading the sharing run batches. This client may not have a Snowflake outbound database configured, or another issue occurred.
          </QESAlert>
        }
        {!batchesQuery.isFetching && !batchesQuery.isError &&
          <Waffles<SharingRunBatchesWaffleToppings>
            columns={sharingRunBatchesColumnConfig}
            customSort={handleSharingRunBatchesCustomSort}
            data={sortedSharingRunBatches.slice(runBatchesSkip, runBatchesSkip + runBatchesPageSize)}
            enablePagination={true}
            fullWidth={true}
            paginationProps={paginationProps}
            passThroughSorting={true}
            sortDirectionOverride={runBatchesSortDirection}
            sortedColumnOverride={runBatchesSortColumn}
            testId="snowflake-data-sharing-run-batches"
          />
        }
      </div>
    );
  }

  const snowflakeIntegration = (): JSX.Element => {
    const nextRunProjectsPaginationProps: WafflesPaginationProps = {
      changePage: (pageNumber) => setNextRunProjectsSkip((pageNumber - 1) * nextRunProjectsPageSize),
      recordsPerPage: nextRunProjectsPageSize,
      skip: nextRunProjectsSkip,
      totalRecords: sortedNextRunProjects.length,
    };

    // A client marked active only counts as active once its share database is confirmed to exist --
    // otherwise the refresh job would just return NotConfigured, so Export and Trigger Refresh stay hidden.
    const isDataShareActive = !!selectedClient?.isDataShareActive;
    const isEffectivelyActive = isDataShareActive && dataShareStatusQuery.isSuccess && dataShareStatusQuery.data.isDatabaseProvisioned;
    const hasSnowflakeConnectivityError = nextRunProjectsQuery.isError;

    return (
      <div className={"snowflake-integration-container"}>
        <div className={"snowflake-integration-selector-row"}>
          <div className={"snowflake-integration-selector-group"}>
            <SingleSelectDropdown<string>
              containerClassName="snowflake-integration-client-selector"
              label="Client"
              items={clientDropdownItems}
              selectedItem={clientDropdownItems.find((item) => item.key === selectedAccountName) ?? { key: "", name: "Select a client" }}
              onSelectionChanged={(item) => handleSelectAccount(item.key)}
              testId="snowflake-data-sharing-integration-client"
            />
            {!!selectedClient &&
              <div className={"snowflake-integration-statuses"}>
                <div className={"snowflake-integration-status"}>
                  <span className={"snowflake-integration-status__label"}>Snowflake Data Share Status</span>
                  {renderDataShareStatusBadge(
                    isEffectivelyActive,
                    `snowflake-integration-status-${selectedClient.accountName}`,
                    isDataShareActive && dataShareStatusQuery.isLoading,
                    isDataShareActive && dataShareStatusQuery.isError,
                  )}
                </div>
                <div className={"snowflake-integration-status"}>
                  <span className={"snowflake-integration-status__label"}>Database Status</span>
                  {renderDataShareDatabaseStatusBadge(
                    dataShareStatusQuery.data?.isDatabaseProvisioned ?? false,
                    `snowflake-integration-database-status-${selectedClient.accountName}`,
                    dataShareStatusQuery.isLoading,
                    dataShareStatusQuery.isError,
                  )}
                </div>
                <div className={"snowflake-integration-status"}>
                  <span className={"snowflake-integration-status__label"}>Listing Status</span>
                  {renderDataShareListingPublishedStatusBadge(
                    dataShareStatusQuery.data?.isListingPublished ?? false,
                    `snowflake-integration-listing-status-${selectedClient.accountName}`,
                    dataShareStatusQuery.isLoading,
                    dataShareStatusQuery.isError,
                  )}
                </div>
              </div>
            }
          </div>
          {!!selectedClient && isEffectivelyActive &&
            <RoleRestricted allowedRoles={[Roles.SNOWFLAKE_DATA_SHARING_ADMIN]}>
              <div className={"snowflake-integration-action-group"}>
                <div className={"snowflake-integration-export"}>
                  <Link className={"snowflake-integration-export__link"} to="/reports" data-testid="snowflake-data-sharing-view-reports">
                    View Reports
                  </Link>
                  <QESButton
                    className="snowflake-integration-refresh-button"
                    disabled={isQueueingExport}
                    onClick={handleExportAsCsv}
                    qesTestId="snowflake-data-sharing-export-csv"
                    qesType="none"
                  >
                    <QesIcon iconName={QesIcons.Download} height="24px" width="24px" isDecorative={true} />
                    Export Shared Data as CSV
                  </QESButton>
                </div>
                <QESButton
                  className="snowflake-integration-refresh-button"
                  disabled={isTriggeringRefresh}
                  onClick={handleTriggerRefresh}
                  qesTestId="snowflake-data-sharing-trigger-refresh"
                  qesType="none"
                >
                  <QesIcon iconName={QesIcons.Zap} height="24px" width="24px" isDecorative={true} />
                  Trigger Refresh
                </QESButton>
              </div>
            </RoleRestricted>
          }
        </div>
        {!!selectedClient &&
          <>
            {!isDataShareActive &&
              <QESAlert type="info" persist={true}>
                There are no projects included in the next refresh because data sharing is inactive for this client.
              </QESAlert>
            }
            <div className={"snowflake-integration-cards"}>
              {snowflakeIntegrationFields.map((field) =>
                <div className={"snowflake-integration-card"} key={field.label}>
                  <span className={"label-tiny"}>{field.label}</span>
                  <span>{hasSnowflakeConnectivityError ? "—" : field.getValue(selectedClient)}</span>
                </div>
              )}
            </div>
            {isDataShareActive &&
              <div className={"clients-table-container"}>
                <h4 className={"header-s"}>Projects Included in the Next Refresh</h4>
                {nextRunProjectsQuery.isFetching &&
                  <div className={"d-flex justify-content-center"}>
                    <LoadingSpinner />
                  </div>
                }
                {!nextRunProjectsQuery.isFetching && nextRunProjectsQuery.isError &&
                  <QESAlert type="danger" persist={true}>
                    There was an error loading the projects included in the next refresh. This client may not have a Snowflake outbound database configured, or another issue occurred.
                  </QESAlert>
                }
                {!nextRunProjectsQuery.isFetching && !nextRunProjectsQuery.isError &&
                  <Waffles<NextRunProjectsWaffleToppings>
                    columns={nextRunProjectsColumnConfig}
                    customSort={handleNextRunProjectsCustomSort}
                    data={sortedNextRunProjects.slice(nextRunProjectsSkip, nextRunProjectsSkip + nextRunProjectsPageSize)}
                    enablePagination={true}
                    fullWidth={true}
                    paginationProps={nextRunProjectsPaginationProps}
                    passThroughSorting={true}
                    sortDirectionOverride={nextRunProjectsSortDirection}
                    sortedColumnOverride={nextRunProjectsSortColumn}
                    testId="snowflake-data-sharing-next-run-projects"
                  />
                }
              </div>
            }
          </>
        }
      </div>
    );
  }

  const tabMap: Record<Tabs, () => JSX.Element> = {
    [Tabs.Dashboard]: dashboard,
    [Tabs.SharingRunMonitor]: sharingRunMonitor,
    [Tabs.SnowflakeIntegration]: snowflakeIntegration,
  };

  const renderActiveTab = (): JSX.Element => {
    const isTabLoading = snowflakeDataSharingContext.isTabLoading?.(activeTab) ?? false;
    const isTabError = snowflakeDataSharingContext.isTabError?.(activeTab) ?? false;

    if (isTabLoading) {
      return (
        <div className={"d-flex justify-content-center"}>
          <LoadingSpinner />
        </div>
      );
    }

    if (isTabError) {
      return (
        <QESAlert type="danger" persist={true}>There was an error loading the data.</QESAlert>
      );
    }

    return tabMap[activeTab]();
  };

  return (
    <Page documentTitle="Snowflake Data Sharing" className="snowflake-data-sharing">
      <Breadcrumbs template={BreadcrumbTemplateEnum.Operations} items={["Snowflake Data Sharing"]} />
      <PageInner>
        <PageHeader title="Snowflake Data Sharing" disableHR={true} />
        <p className={"label-s"}>Monitor and manage Snowflake data sharing between Quest Analytics and its clients.</p>
        <PageSection>
            <div className="tabs-style-2 mb-3">
              <ul className="nav nav-tabs">
                {tabs}
              </ul>
            </div>
        </PageSection>
        <PageSection>
          {renderActiveTab()}
        </PageSection>
      </PageInner>
    </Page>
  );
};